import React from 'react';
import _ from 'lodash';

import * as YVS from 'components/line-items/controls/year-values-styles';
import { RightArrow, DownArrow } from '../../../../images/icons/tools';

import { evenlyDistribute, tryParseFloat, localizeNum } from "data/lib/numbers"

export default class YearColumn extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      yearData: this.props.yearData,
      yearViewData: this.localizeYearData(this.props.yearData),
      total: this.calculateTotal(this.props.yearData)
    }
  }

  componentDidMount() {
    document.addEventListener('rdcf_cello_resetButtonClicked', this.reset);
  }

  componentWillUnmount() {
    document.removeEventListener('rdcf_cello_resetButtonClicked', this.reset);
  }

  componentWillReceiveProps(nextProps) {
    // only update initial data from parent if it is a cascade update or if a reset signal appears
    if(nextProps.isCascadeUpdate || nextProps.none) {
      this.setState({yearData: nextProps.yearData, yearViewData: this.localizeYearData(nextProps.yearData), total: this.calculateTotal(nextProps.yearData)});
    }
  }

  localizeYearData = yearData => {
    let localizedYear = Object.assign({}, yearData);
    for(let i = 1; i <= 12; i++) {
      localizedYear[`m${i}`] = localizeNum(localizedYear[`m${i}`], 4);
    }
    return localizedYear;
  }

  handleMonthInput = (monthOrdinal, value) => {
    const updated = Object.assign({}, this.state.yearViewData);
    updated[`m${monthOrdinal}`] = value;
    this.setState({yearViewData: updated});
  }

  updateSingleValue = (monthOrdinal, callback) => {
    const updated = Object.assign({}, this.state.yearData);
    updated[`m${monthOrdinal}`] = tryParseFloat(this.state.yearViewData[`m${monthOrdinal}`], 4);
    this.setState({yearData: updated, yearViewData: this.localizeYearData(updated), total: this.calculateTotal(updated)}, () => {
      this.props.updateYear(this.state.yearData, callback);
    });
  }

  cascadeValuesDown = monthOrdinal => {
    if(monthOrdinal < 12) {
      const updated = Object.assign({}, this.state.yearData);
      const value = tryParseFloat(this.state.yearViewData[`m${monthOrdinal}`], 4);
      for(let i = monthOrdinal + 1; i <= 12; i++) {
        const month = `m${i}`;
        updated[month] = value;
      }
      this.setState({yearData: updated, yearViewData: this.localizeYearData(updated), total: this.calculateTotal(updated)}, () => this.props.updateYear(this.state.yearData));
    }
  }

  calculateTotal = yearData => {
    const months = Object.assign({}, yearData);
    delete months.year;
    const monthValues = Object.values(months);
    let reduced = monthValues.reduce((total, current) => total + current);
    reduced = this.props.isPercent ? reduced/12 : reduced
    let total = tryParseFloat(reduced, 4);
    return localizeNum(total, 4);
  }

  spreadTotal = callback => {
    const updated = Object.assign({}, this.state.yearData);
    const total = tryParseFloat(this.state.total.replace(/,/g, ''), 4);
    const bucketValues = evenlyDistribute(this.props.isPercent ? total*12 : total, 12, 4);
    for (let i = 1; i < 13; i++) {
      updated[`m${i}`] = bucketValues.pop();
    }
    this.setState({yearData: updated, yearViewData: this.localizeYearData(updated), total: localizeNum(total, 4)}, () => {
      this.props.updateYear(this.state.yearData, callback);
    });
  }

  handleMonthKeyDown = (e, monthOrdinal) => {
    // because of the custom tab and enter requirements, we have to manually control tab direction
    if(e.keyCode === 9 || e.keyCode === 13) {
      e.preventDefault();
      let next = true;
      if(e.shiftKey) {
        next = false;
      }
      this.updateSingleValue(monthOrdinal);
    }
    // handle hotkey cascade down for shift + ctrl + x
    if(e.shiftKey && e.ctrlKey && e.keyCode === 88) {
      const cascade = () => setTimeout(() => this.cascadeValuesDown(monthOrdinal), 1)
      this.updateSingleValue(monthOrdinal, cascade)
    }
  }

  handleTotalKeyDown = e => {
    // because of the custom tab and enter requirements, we have to manually control tab direction
    if(e.keyCode === 9 || e.keyCode === 13) {
      e.preventDefault();
      // this will cause the total field on the last column to exit to the next cello cell
      if(!e.shiftKey && e.keyCode === 9 && this.props.isLastColumn) this.spreadTotal(() => this.props.lastFieldTabHandler());
    }
    // handle hotkey cascade right for shift + ctrl + x
    if(e.shiftKey && e.ctrlKey && e.keyCode === 88) {
      this.props.cascadeValuesRight(this.state.yearData);
    }
  }

  // spread the total if the value changed, otherwise do nothing
  handleTotalBlur = e => {
    if(this.state.totalChanged) {
      this.spreadTotal();
      this.setState({totalChanged: false});
    }
  }

  render() {
    const { analysisBeginDate } = this.props;
    const currentMonth          = analysisBeginDate.month();
    const currentYear           = analysisBeginDate.year();
    const diff                  = this.state.yearData.year - 1;
    const yearHeader            = currentMonth === 0 ? currentYear + diff : `${currentYear + diff}-${currentYear + diff + 1}`

    const months = Object.assign({}, this.state.yearViewData);
    delete months.year;
    let monthValues = []
    for(let i = 1; i < 13; i++) {
      monthValues.push(months[`m${i}`])
    }

    const monthInputs = monthValues.map((m, i) => {
      const value = _.isFinite(m) ? m.toFixed(4) : m;
      return (
        <YVS.Input key={`month-input-${i}`}>
          {/* Month Inputs */}
          <input
            type="text"
            className="month-input automated"
            value={value}
            onKeyDown={e => this.handleMonthKeyDown(e, i+1)}
            onChange={e => this.handleMonthInput(i+1, e.target.value)}
            onBlur={e => this.updateSingleValue(i+1)}
            onFocus={e => e.target.select()}
            ref={month => this[`m${i+1}`] = month}
            {...this.props.attrs([this.props.yearNum, `m${i+1}`])}/>
          {i !== 11 &&
          <span className="caret-down" title="ctrl + shift + x" onClick={() => this.cascadeValuesDown(i+1)}>
            <img src={DownArrow}/>
          </span>}
        </YVS.Input>
      );
    });

    const column = (
      <div className="year-column">
        <div className="year-header">{yearHeader}</div>
        { monthInputs }
        <YVS.Input>
          {/* Total Input */}
          <input
            type="text"
            className="year-input automated-secondary"
            value={this.state.total}
            onChange={e => this.setState({total: e.target.value, totalChanged: true})}
            onKeyDown={this.handleTotalKeyDown}
            onBlur={this.handleTotalBlur}
            ref={totalInput => this.totalInput = totalInput}/>
          {!this.props.isLastColumn &&
            <span
              className="caret-right"
              title="ctrl + shift + x"
              onClick={() => this.props.cascadeValuesRight(this.state.yearData)}>
              <img src={RightArrow}/>
            </span>}
        </YVS.Input>
      </div>
    );

    return column;
  }
}
